import {
  MachineList,
  MachineItem,
  OverrideObject,
  Report,
  ReportObject,
  Target,
  FleetmonitorKpiAchievements
} from "interfaces/kpis";

export const getTargetedAndOthers = (
  targets: Target[],
  reports: Report[],
  fmMeasurements: FleetmonitorKpiAchievements[]
): MachineList => {
  let targeted: MachineItem[] = [];
  const othersMap = new Map<number | string, MachineItem>();

  // Process targets first
  targeted = targets.map(target => {
    const report = reports.find(report => report.machine === target.machine);
    const measurements = fmMeasurements.filter(
      measurement => measurement.machine === target.machine
    );
    let reportObject: ReportObject | undefined = undefined;
    let overrideObject: OverrideObject | undefined = undefined;
    let hasReport = !!report;
    let hasMeasurements = !!measurements.length;

    const {
      is_overridden,
      override_approver_detail,
      override_date,
      override_justification,
      ...targetObject
    } = target;

    if (report) {
      reportObject = {
        date: report.report_approval_date,
        id: report.id,
        link: report.report_link,
        title: report.report_name,
        userName: report.report_approver
      };
    }

    if (is_overridden) {
      overrideObject = {
        date: override_date ?? "",
        justification: override_justification ?? "",
        userName: `${override_approver_detail?.first_name} ${override_approver_detail?.last_name}`
      };
    }

    return {
      ...targetObject,
      hasOverride: is_overridden,
      override: overrideObject,
      hasReport,
      report: reportObject,
      hasMeasurements,
      measurements: measurements.flatMap(m => m.measurement_updates)
    };
  });

  // Process non-targeted reports
  for (const report of reports) {
    if (!targets.some(target => target.machine === report.machine)) {
      const {
        report_approval_date,
        report_approver,
        report_id,
        report_link,
        report_name,
        ...reportObject
      } = report;

      const reportItem: MachineItem = {
        ...reportObject,
        hasOverride: false,
        override: undefined,
        hasReport: true,
        report: {
          date: report_approval_date,
          id: report_id,
          link: report_link,
          title: report_name,
          userName: report_approver
        },
        hasMeasurements: false,
        measurements: []
      };

      othersMap.set(report.machine, reportItem);
    }
  }

  // Process non-targeted measurements and combine with existing reports
  for (const measurement of fmMeasurements) {
    if (!targets.some(target => target.machine === measurement.machine)) {
      const { measurement_updates, ...measurementObject } = measurement;

      if (othersMap.has(measurement.machine)) {
        // Combine with existing report
        const existingItem = othersMap.get(measurement.machine)!;
        othersMap.set(measurement.machine, {
          ...existingItem,
          hasMeasurements: true,
          measurements: measurement_updates
        });
      } else {
        // Create new measurement-only item
        othersMap.set(measurement.machine, {
          ...measurementObject,
          hasOverride: false,
          override: undefined,
          hasReport: false,
          report: undefined,
          hasMeasurements: true,
          measurements: measurement_updates
        });
      }
    }
  }

  return {
    targeted: targeted.sort((a, b) =>
      a.machine_name.localeCompare(b.machine_name)
    ),
    others: Array.from(othersMap.values()).sort((a, b) =>
      a.machine_name.localeCompare(b.machine_name)
    )
  };
};
